/**
 * "Assemblies matched by this customer's parts", under the customer on the case page.
 *
 * Assemblies matched by parts the customer was invoiced or quoted before this inquiry,
 * one row per assembly family (with its near-identical variants), a tier badge
 * (Invoiced / Strong match / Possible) and the last evidence day. Expanding a row lists
 * the parts behind it. Lines of this case whose item belongs to one of the assemblies
 * read "Part of ...". With the companion switch on, "Often quoted with" follows.
 *
 * Display only: nothing here changes a line, the customer or the draft. Read from the
 * server only when the disclosure opens.
 */

import { Badge, Collapsible, Skeleton, Typography } from "@norican/react";
import { useState } from "react";

import {
  type InstalledBaseFamily,
  installedBaseLead,
  type InstalledBaseView,
  partSourceLabel,
  tierTone,
  useCaseInstalledBase,
  variantsText,
} from "@/lib/api";
import { formatDay } from "@/lib/work-labels";

function FamilyRow({ family }: { family: InstalledBaseFamily }) {
  const [open, setOpen] = useState(false);
  const tokens = (family.model_tokens ?? []).slice(0, 4);
  const variants = variantsText(family);
  const evidence = family.evidence ?? [];
  return (
    <li className="border-border-subtle flex flex-col gap-1 border-b pb-2 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={tierTone(family.tier)} variant="subtle" size="xs">
          {family.tier_label}
        </Badge>
        <span className="text-fg min-w-0 text-sm font-semibold break-words">{family.label}</span>
        {tokens.length > 0 && (
          <span className="text-fg-muted font-mono text-xs">{tokens.join(" ")}</span>
        )}
      </div>
      <Typography variant="caption" tone="muted" className="break-words">
        {[
          `${family.n_parts ?? 0} matched ${family.n_parts === 1 ? "part" : "parts"}`,
          variants,
          family.last_evidence_at ? `last ${formatDay(family.last_evidence_at)}` : "",
        ]
          .filter(Boolean)
          .join(" · ")}
      </Typography>
      {evidence.length > 0 && (
        <Collapsible.Root open={open} onOpenChange={setOpen}>
          <Collapsible.Trigger className="text-accent-fg-subtle w-fit text-xs font-semibold hover:underline">
            {open ? "Hide the items" : "Show the items"}
          </Collapsible.Trigger>
          <Collapsible.Panel>
            <ul className="mt-1 flex flex-col gap-0.5">
              {evidence.map((part) => (
                <li key={part.item} className="text-fg-muted text-xs break-words">
                  <span className="text-fg font-mono">{part.item}</span>
                  {part.name ? ` ${part.name}` : ""}
                  {`. ${partSourceLabel(part.src)}, first ${formatDay(part.first_at)}`}
                  {part.last_at !== part.first_at ? `, last ${formatDay(part.last_at)}` : ""}
                </li>
              ))}
            </ul>
            <Typography variant="caption" tone="subtle" className="mt-1 break-all">
              Assembly {(family.parents ?? [family.parent]).join(", ")}
            </Typography>
          </Collapsible.Panel>
        </Collapsible.Root>
      )}
    </li>
  );
}

function Body({ view }: { view: InstalledBaseView }) {
  const families = view.families ?? [];
  const links = view.line_links ?? [];
  const companions = view.companions ?? [];
  return (
    <div className="mt-2 flex flex-col gap-2">
      <Typography variant="caption" tone="muted" className="break-words">
        {installedBaseLead(view)}
      </Typography>
      {families.length > 0 && (
        <ul className="flex flex-col gap-2">
          {families.map((family) => (
            <FamilyRow key={family.family_id} family={family} />
          ))}
        </ul>
      )}
      {links.length > 0 && (
        <div className="flex flex-col gap-0.5">
          <Typography variant="caption" tone="subtle">
            Lines of this case
          </Typography>
          {links.map((link) => (
            <Typography
              key={`${link.line_key}-${link.item}`}
              variant="caption"
              tone="muted"
              className="break-words"
            >
              <span className="text-fg font-mono">{link.item}</span> Part of {link.family_label}
            </Typography>
          ))}
        </div>
      )}
      {view.companions_on && companions.length > 0 && (
        <div className="flex flex-col gap-0.5">
          <Typography variant="caption" tone="subtle">
            Often quoted with (suggestions, not added to the quotation)
          </Typography>
          {companions.map((c) => (
            <Typography key={c.item} variant="caption" tone="muted" className="break-words">
              <span className="text-fg font-mono">{c.item}</span>
              {c.name ? ` ${c.name}` : ""} ({c.reason}, with {c.anchor})
            </Typography>
          ))}
        </div>
      )}
    </div>
  );
}

/** The disclosure under the customer. Hidden when the panel switch is off. */
export function InstalledBasePanel({ caseId }: { caseId: string }) {
  const [open, setOpen] = useState(false);
  const query = useCaseInstalledBase(caseId, open);
  const view = query.data ?? null;
  if (view && !view.enabled) {
    return null;
  }
  const count = view?.families?.length ?? 0;
  return (
    <Collapsible.Root open={open} onOpenChange={setOpen}>
      <Collapsible.Trigger className="text-accent-fg-subtle text-xs font-semibold hover:underline">
        {open
          ? "Hide assemblies matched by this customer's items"
          : `Assemblies matched by this customer's items${count > 0 ? ` (${count})` : ""}`}
      </Collapsible.Trigger>
      <Collapsible.Panel>
        {query.isLoading ? (
          <Skeleton variant="rect" height={48} className="mt-2" />
        ) : query.isError ? (
          <Typography variant="caption" tone="muted" role="alert" className="mt-2">
            I2Q could not read the item history now.
          </Typography>
        ) : !view ? (
          <Typography variant="caption" tone="muted" className="mt-2">
            Not available for this case.
          </Typography>
        ) : (
          <Body view={view} />
        )}
      </Collapsible.Panel>
    </Collapsible.Root>
  );
}
